/**
 * useNfc — lecture/écriture des Cartes d'Aventurier (puce NFC).
 *
 * Réalité des plateformes (voir docs/nfc-carte-aventurier.md) :
 *  - Web NFC (NDEFReader) n'existe que sur Chrome ANDROID, en contexte
 *    sécurisé (HTTPS ou localhost). Pas de desktop, pas d'iOS, pas de
 *    WebView Capacitor (il faudra un plugin natif pour l'APK).
 *  - D'où : détection stricte + chemin de SIMULATION (dev) qui traverse
 *    exactement le même code que la lecture réelle — la logique métier est
 *    testable partout, seul le tap physique exige un appareil compatible.
 *
 * Format d'une carte (enregistrement NDEF texte, même esprit que le QR
 * `GUILD_QUEST:`) : `GUILD_CARTE_V2:<publicId>`. L'ancien format est encore
 * lu pour les cartes déjà gravées, mais n'est plus écrit.
 */
import { ref } from 'vue';

// Web NFC (Chrome Android uniquement) — pas de types publiés dans la lib DOM.
declare const NDEFReader: any;

export const NFC_CARD_PREFIX = 'GUILD_CARTE:';
export const NFC_CARD_V2_PREFIX = 'GUILD_CARTE_V2:';

export const nfcSupported = typeof window !== 'undefined'
  && 'NDEFReader' in window
  && window.isSecureContext === true;

export const nfcScanning = ref(false);

let _abort: AbortController | null = null;
let _onCard: ((card: any) => void) | null = null;

/** Parse une puce, un ancien Sceau ou l'URL HTTPS ouverte par le QR. */
export function parseCardText(text: any) {
  if (typeof text !== 'string') return null;
  const value = text.trim();
  if (value.startsWith(NFC_CARD_V2_PREFIX)) {
    const publicId = value.slice(NFC_CARD_V2_PREFIX.length).trim();
    return /^[A-Za-z0-9_-]{20,64}$/.test(publicId) ? { accountId: publicId, publicId, nom: '' } : null;
  }
  try {
    const url = new URL(value);
    // Accepte aussi une origine déployée sous un préfixe de chemin, tout en
    // exigeant que le Sceau termine bien l'URL. Les paramètres de suivi sont
    // ignorés par URL.pathname.
    const match = url.pathname.match(/(?:^|\/)carte\/([A-Za-z0-9_-]{20,64})\/?$/);
    if (match) return { accountId: match[1], publicId: match[1], nom: '', url: url.toString() };
  } catch { /* ce n'est pas une URL : essayer le format historique */ }
  if (!value.startsWith(NFC_CARD_PREFIX)) return null;
  const rest = value.slice(NFC_CARD_PREFIX.length);
  const sep = rest.indexOf(':');
  const accountId = sep >= 0 ? rest.slice(0, sep) : rest;
  const nom = sep >= 0 ? rest.slice(sep + 1) : '';
  if (!accountId) return null;
  return { accountId, nom };
}

/**
 * Démarre l'écoute NFC. `onCard({ accountId, nom })` est appelé à chaque
 * carte de guilde lue. Retourne false si le NFC n'est pas disponible ici.
 */
export async function startNfcScan(onCard: (card: any) => void) {
  if (!nfcSupported) return false;
  stopNfcScan();
  _onCard = onCard;
  _abort = new AbortController();
  try {
    const reader = new NDEFReader();
    reader.onreading = (event: any) => {
      for (const record of event.message?.records || []) {
        if (record.recordType !== 'text') continue;
        try {
          const text = new TextDecoder(record.encoding || 'utf-8').decode(record.data);
          const card = parseCardText(text);
          if (card && _onCard) _onCard(card);
        } catch { /* enregistrement illisible : carte étrangère, on ignore */ }
      }
    };
    await reader.scan({ signal: _abort.signal });
    nfcScanning.value = true;
    return true;
  } catch (err: any) {
    console.warn('[NFC] scan impossible :', err?.message);
    nfcScanning.value = false;
    return false;
  }
}

export function stopNfcScan() {
  if (_abort) { try { _abort.abort(); } catch {} }
  _abort = null;
  _onCard = null;
  nfcScanning.value = false;
}

/**
 * Grave l'identité donnée sur une carte vierge approchée du téléphone.
 * Android Chrome uniquement. Retourne { success } ou { error }.
 */
export async function writeAdventurerCard(publicId: string) {
  if (!nfcSupported) return { error: 'NFC indisponible sur cet appareil/navigateur.' };
  if (!/^[A-Za-z0-9_-]{20,64}$/.test(publicId || '')) return { error: 'Sceau public inconnu.' };
  try {
    const reader = new NDEFReader();
    await reader.write({
      records: [{ recordType: 'text', data: `${NFC_CARD_V2_PREFIX}${publicId}` }]
    });
    return { success: true };
  } catch (err: any) {
    return { error: err?.message || 'Écriture NFC échouée.' };
  }
}

/**
 * SIMULATION (dev/démo) : injecte une lecture de carte dans le handler
 * actif — même chemin de code que le vrai tap. Si aucun scan n'est actif,
 * le handler passé en second argument est appelé directement.
 */
export function simulateCardTap(card: any, fallbackHandler?: (card: any) => void) {
  const parsed = card && card.accountId ? card : parseCardText(String(card));
  if (!parsed) return false;
  if (_onCard) { _onCard(parsed); return true; }
  if (fallbackHandler) { fallbackHandler(parsed); return true; }
  return false;
}
